import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MoreVertical, ArrowLeft } from 'lucide-react';
import { getMyAds, toggleAd, deleteAd, takeABreak, publishAd } from '../../services/p2pService';
import { useToast } from '../../context/ToastContext';

export default function MyAds() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [ads, setAds] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [onBreak, setOnBreak] = useState(false);
  const [menuId, setMenuId] = useState<string | null>(null);
  const [hideCancelled, setHideCancelled] = useState(true);

  const load = () => {
    setLoading(true);
    getMyAds()
      .then(data => { setAds(data); setOnBreak(data.some((a: any) => a.isTakeBreak)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleToggle = async (id: string) => {
    try { await toggleAd(id); load(); } catch (e: any) { toast(e.message, 'error'); }
  };
  const handleDelete = async (id: string) => {
    try { await deleteAd(id); toast('Ad cancelled', 'success'); load(); } catch (e: any) { toast(e.message, 'error'); }
  };
  const handleBreak = async () => {
    try { await takeABreak(!onBreak); setOnBreak(!onBreak); load(); toast(onBreak ? 'Ads resumed' : 'All ads paused', 'success'); } catch (e: any) { toast(e.message, 'error'); }
  };
  const handlePublish = async (id: string) => {
    try { await publishAd(id); toast('Ad published!', 'success'); load(); } catch (e: any) { toast(e.message, 'error'); }
  };

  const statusColor: Record<string, string> = {
    active: '#1CD4A7', paused: '#F59E0B', draft: '#71717A', cancelled: '#F6465D', completed: '#3B82F6',
  };

  const displayAds = hideCancelled ? ads.filter(a => a.status !== 'cancelled') : ads;

  return (
    <div className="p2p-page">
      {/* Header */}
      <div className="p2p-header">
        <button onClick={() => navigate('/home')} style={{ background: 'none', border: 'none', color: 'var(--p2p-text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px 8px 4px 0' }}><ArrowLeft size={20} /></button>
        <span className="p2p-header-title">My Ads</span>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={handleBreak}
            style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: '1px solid', borderColor: onBreak ? 'var(--p2p-sell)' : 'var(--p2p-border-input)', background: onBreak ? 'var(--p2p-sell-subtle)' : 'transparent', color: onBreak ? 'var(--p2p-sell)' : 'var(--p2p-text-secondary)', cursor: 'pointer' }}
          >
            {onBreak ? 'Resume All' : 'Take a Break'}
          </button>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}>
            <span style={{ fontSize: 11, color: 'var(--p2p-text-muted)', whiteSpace: 'nowrap' }}>Hide Cancelled</span>
            <input
              type="checkbox"
              checked={hideCancelled}
              onChange={() => setHideCancelled(!hideCancelled)}
              style={{ display: 'none' }}
            />
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              width: 40, height: 22, borderRadius: 11, flexShrink: 0,
              background: hideCancelled ? '#1CD4A7' : '#3d4148',
              transition: 'background 0.2s',
              padding: '0 3px',
              boxSizing: 'border-box',
            }}>
              <span style={{
                display: 'block',
                width: 16, height: 16, borderRadius: '50%',
                background: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                transform: hideCancelled ? 'translateX(18px)' : 'translateX(0px)',
                transition: 'transform 0.2s',
                flexShrink: 0,
              }} />
            </span>
          </label>
          <button
            onClick={() => navigate('/p2p/post-ad/1')}
            style={{ width: 38, height: 38, minWidth: 38, minHeight: 38, borderRadius: '50%', background: 'var(--p2p-buy)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, padding: 0, lineHeight: 1 }}
          >
            <Plus size={20} color="#000" />
          </button>
        </div>
      </div>

      <div className="p2p-page-content">
      {loading ? <div>{[...Array(4)].map((_, i) => (
          <div key={i} className="p2p-skel-card" style={{ flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <span className="p2p-skel" style={{ width: 36, height: 20, borderRadius: 4 }} />
                <span className="p2p-skel" style={{ width: 80, height: 20 }} />
                <span className="p2p-skel" style={{ width: 50, height: 20, borderRadius: 4 }} />
              </div>
              <span className="p2p-skel" style={{ width: 20, height: 20, borderRadius: 4 }} />
            </div>
            <span className="p2p-skel" style={{ width: 130, height: 22 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <span className="p2p-skel" style={{ width: 140, height: 10 }} />
              <span className="p2p-skel" style={{ width: 120, height: 10 }} />
            </div>
            <span className="p2p-skel" style={{ width: 100, height: 11 }} />
          </div>
        ))}</div>
        : ads.length === 0 ? (
          <div className="p2p-empty">
            <p className="p2p-empty-text">No ads yet</p>
            <button className="p2p-btn-yellow" style={{ marginTop: 16, width: 200 }} onClick={() => navigate('/p2p/post-ad/1')}>Post an Ad</button>
          </div>
        ) : (
          <div>
            {displayAds.map(ad => {
              const isEditable = ['active', 'paused', 'draft'].includes(ad.status);
              const canToggle = ['active', 'paused'].includes(ad.status);
              const pm = ad.paymentMethods ?? [];
              const sColor = statusColor[ad.status];

              return (
                <div key={ad._id} style={{ padding: '12px 16px', borderBottom: '1px solid var(--p2p-border-default)', position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>

                    {/* LEFT COLUMN */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Line 1: side badge + pair + status */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                        <span className={`p2p-badge p2p-badge-${ad.side === 'buy' ? 'buy' : 'sell'}`}>{ad.side.toUpperCase()}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--p2p-text-primary)' }}>{ad.crypto}/{ad.fiat}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: `${sColor}20`, color: sColor }}>{ad.status}</span>
                      </div>
                      {/* Price */}
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 3 }}>
                        <span className="p2p-ad-price">{(ad.currentPrice ?? ad.fixedPrice ?? 0).toLocaleString()}</span>
                        <span className="p2p-ad-fiat-label">{ad.fiat}/{ad.crypto}</span>
                      </div>
                      {/* Limits + Available */}
                      <div className="p2p-ad-limits">Limit: {ad.minOrderAmount?.toLocaleString()} – {ad.maxOrderAmount?.toLocaleString()} {ad.fiat}</div>
                      <div className="p2p-ad-limits">Available: {parseFloat((ad.availableAmount ?? ad.totalAmount ?? 0).toFixed(4)).toString()} {ad.crypto}</div>
                      {/* Payment methods */}
                      {pm.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                          {pm.map((m: any, i: number) => (
                            <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--p2p-text-secondary)' }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.methodColor ?? '#2563eb', flexShrink: 0 }} />
                              {m.methodType ?? m}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* RIGHT COLUMN */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                      {/* Menu button */}
                      {isEditable && (
                        <button onClick={() => setMenuId(menuId === ad._id ? null : ad._id)} style={{ background: 'none', border: 'none', color: 'var(--p2p-text-muted)', cursor: 'pointer', padding: 4 }}>
                          <MoreVertical size={16} />
                        </button>
                      )}
                      {/* Draft publish */}
                      {ad.status === 'draft' && ad.totalAmount > 0 && (
                        <button onClick={() => handlePublish(ad._id)} style={{ fontSize: 12, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--p2p-buy)', background: 'var(--p2p-buy-subtle)', color: 'var(--p2p-buy)', cursor: 'pointer' }}>
                          Publish
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Dropdown Menu */}
                  {menuId === ad._id && (
                    <div style={{ position: 'absolute', right: 16, top: 44, background: 'var(--p2p-bg-elevated)', border: '1px solid var(--p2p-border-default)', borderRadius: 8, zIndex: 10, overflow: 'hidden', minWidth: 150 }}>
                      {ad.status === 'draft' && (
                        <>
                          <button onClick={() => { sessionStorage.setItem('p2p_draft_id', ad._id); navigate('/p2p/post-ad/2'); setMenuId(null); }} style={{ display: 'block', width: '100%', padding: '12px 16px', background: 'none', border: 'none', color: 'var(--p2p-buy)', cursor: 'pointer', textAlign: 'left', fontSize: 13 }}>Continue Draft</button>
                          <button onClick={() => { handleDelete(ad._id); setMenuId(null); }} style={{ display: 'block', width: '100%', padding: '12px 16px', background: 'none', border: 'none', color: 'var(--p2p-sell)', cursor: 'pointer', textAlign: 'left', fontSize: 13 }}>Delete Draft</button>
                        </>
                      )}
                      {canToggle && (
                        <>
                          <button onClick={() => { handleToggle(ad._id); setMenuId(null); }} style={{ display: 'block', width: '100%', padding: '12px 16px', background: 'none', border: 'none', color: 'var(--p2p-text-primary)', cursor: 'pointer', textAlign: 'left', fontSize: 13 }}>
                            {ad.status === 'active' ? 'Pause Ad' : 'Resume Ad'}
                          </button>
                          <button onClick={() => { handleDelete(ad._id); setMenuId(null); }} style={{ display: 'block', width: '100%', padding: '12px 16px', background: 'none', border: 'none', color: 'var(--p2p-sell)', cursor: 'pointer', textAlign: 'left', fontSize: 13 }}>Cancel Ad</button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
